import { PricingPlans } from '@constants/pricing';
import { DateRange } from './filter';

export interface ProjectDetails {
  project_name?: string;
  project_short_desc?: string;
  project_long_desc?: string;
  project_insights?: string;
  visual_settings?: {
    ['3d_charts']?: boolean;
    gradient_charts?: boolean;
    multicolor_charts?: boolean;
  };
}

export interface ProjectData {
  auto_charts_executed: boolean;
  colorset: any;
  created_at: string;
  created_by: string;
  id: string;
  project_has_addon_fields: boolean;
  project_has_auto_categorized_fields: boolean;
  chart_id_order: string[];
  project_customer: string;
  project_datafiles: ProjectDataFile[];
  project_is_adhoc_subscription: boolean;
  project_is_deleted: boolean;
  project_is_private: boolean;
  project_long_desc: string;
  project_name: string;
  project_owner_user: number;
  project_short_desc: string;
  project_status: string;
  project_team: string;
  updated_at: string;
  updated_by: number;
  filters: any;
  dynamic_filters: any;
  visual_settings: {
    ['3d_charts']: boolean;
    gradient_charts: boolean;
    multicolor_charts: boolean;
  };
  project_imported_columns: number;
  project_total_columns: number;
  project_remark: string;
  project_insights: string;
  project_data_updated_at: string;
  is_api_connected: boolean;
  date_range: { [key: string]: DateRange };
  plan_at_creation: {
    is_trial: boolean;
    mkt_plan_code: any;
    plan_code: PricingPlans;
  };
}

export interface ProjectDataFile {
  created_at: string;
  file_status: string;
  file_columns: number;
  file_rows: number;
  filename: string;
  filetype: string;
  id: string;
  is_active: boolean;
}

export interface GetProjectsResponse {
  auto_charts_executed: boolean;
  colorset: any;
  created_at: string;
  created_by: string;
  id: string;
  project_customer: string;
  project_datafiles: string[];
  project_is_adhoc_subscription: boolean;
  project_is_deleted: boolean;
  project_is_private: boolean;
  project_long_desc: string;
  project_name: string;
  project_owner_user: number;
  project_short_desc: string;
  project_status: string;
  project_team: string;
  updated_at: string;
  updated_by: number;
}

export interface UpdateProjectResponse {
  project_name: string;
  project_short_desc: string;
  project_long_desc: string;
  updated_at: string;
  updated_by: number;
  visual_settings: any;
  error: any;
}

export interface CleanedRow {
  dirt_quantity: number;
  dirt_examples?: any;
  dirt_type: string;
}

export interface CleanResponse {
  project_name: string;
  file: File;
  file_type: string;
  cleaned_rows: CleanedRow[];
  cleaned_columns: CleanedColumn[];
}

export interface CleanedColumn {
  id: string;
  fieldname: string;
  file_fieldname: string;
  to_import: boolean;
  contains_sensitive: boolean;
  empty_rows: number;
  unique_values: number;
  cleaned_types: CleanedTypes;
}

export interface CleanedTypes {
  ANONYMIZATION?: string;
  MISSING_VALUES?: string;
}

export interface File {
  file_rows: number;
  imported_rows: number;
  file_columns: number;
  imported_columns: number;
  is_test_template: boolean;
  deletedfile: boolean;
  detected_industry: any;
  detected_file_content: any;
  confirmed_industry: any;
  confirmed_file_content: any;
}

export enum AvailableRadioOptions {
  ADD = 'ADD',
  UPDATE = 'UPDATE',
  BOTH = 'BOTH',
  NONE = 'NONE'
}
export interface CombineDataType {
  id: string;
  source_datafile: Datafile;
  target_datafile: Datafile;
  add_rows: AvailableRadioOptions;
  add_columns: AvailableRadioOptions;
  add_columns_frozen: boolean;
  duplicate_rows_for_key_field: boolean;
  duplicate_rows_frozen: boolean;
  source_file_keyfields: any;
  target_file_keyfields: any;
  selected_source_keyfield: any;
  selected_target_keyfield: any;
  initial_target_file_id: string;
}

export interface Datafile {
  filename: string;
  filetype: string;
  id: string;
  file_status: string;
}

export interface CleanPageDataResponse {
  project_id: string;
  project_name: string;
  file: File;
  dirt_rows: Dirtrow[];
  dirt_columns: Dirtcolumn[];
}

export interface Dirtcolumn {
  id: string;
  fieldname: string;
  data_facts: string;
  treat_as_type: string;
  dirt_types: Dirttypes;
  cleaned_count: Cleanedcount;
  clean_action: Cleanaction;
  clean_action_choices: Cleanactionchoices;
  default_action_choice: Dirttypes;
}

export interface Cleanactionchoices {
  MISSING_VALUES?: string[][];
  DATE_FORMAT?: string[][];
  TRIM_WHITESPACE?: string[][];
  CASING_FORMAT?: string[][];
  DATA_VARIATIONS?: string[][];
  MULTI_CURRENCY_FORMAT?: string[][];
  ANONYMIZATION?: string[][];
  PERCENTAGE_VALUES?: string[][];
  CURRENCY_FORMAT?: string[][];
}

export interface Cleanaction {
  MISSING_VALUES?: any;
  DATE_FORMAT?: any;
  TRIM_WHITESPACE?: any;
  CASING_FORMAT?: any;
  DATA_VARIATIONS?: any;
  MULTI_CURRENCY_FORMAT?: any;
  ANONYMIZATION?: any;
  PERCENTAGE_VALUES?: any;
  CURRENCY_FORMAT?: any;
}

export interface Cleanedcount {
  MISSING_VALUES?: number;
  DATE_FORMAT?: number;
  TRIM_WHITESPACE?: number;
  CASING_FORMAT?: number;
  DATA_VARIATIONS?: number;
  MULTI_CURRENCY_FORMAT?: number;
  ANONYMIZATION?: number;
  PERCENTAGE_VALUES?: number;
  CURRENCY_FORMAT?: number;
}

export interface Dirttypes {
  MISSING_VALUES?: string;
  DATE_FORMAT?: string;
  TRIM_WHITESPACE?: string;
  CASING_FORMAT?: string;
  DATA_VARIATIONS?: string;
  MULTI_CURRENCY_FORMAT?: string;
  ANONYMIZATION?: string;
  PERCENTAGE_VALUES?: string;
  CURRENCY_FORMAT?: string;
}

export interface Dirtrow {
  dirt_quantity: number;
  dirt_examples?: string;
  dirt_type: string;
  clean_action?: any;
  clean_action_choices: string[][];
  default_action_choice: string;
}

export interface FileInfo {
  file_rows: number;
  imported_rows: number;
  file_columns: number;
  imported_columns: number;
}

export interface SqBotData {
  message: string;
  sqb_id: string;
  chart_id: string;
  action: string;
  button_text: string;
  prompt: string;
  rating: number;
  draftPrompt: string;
  usedButton: boolean;
}

export interface Sheet {
  properties: {
    sheetId: number;
    title: string;
  };
}

export interface Spreadsheet {
  properties: {
    title: string;
  };
  sheets: Sheet[];
}
